import { c as _c } from "react/compiler-runtime";
import { type MessagesState } from '../context/context';
import { useConversationsSelector, useConversationsStore } from '../context/ConversationsContext';

// The conversations hooks. Each one subscribes to as little as it can: the
// actions to nothing, the others to one slice of the store. useConversations
// is the old all-in-one hook, kept for callers that have not moved yet; it
// rerenders on every change to any conversation.

const initialMessagesState: MessagesState = {
  messages: [],
  cursor: undefined,
  hasNextPage: true,
  hasNewerPage: false,
  heldCount: 0,
  isLoading: false,
  error: null
};

/** Everything that changes conversations. Never rerenders the caller. */
export const useConversationActions = () => {
  return useConversationsStore().actions;
};

/** The conversation list. */
export const useConversationList = () => {
  return useConversationsSelector(_temp);
};

/** One conversation, or null when it is not in the list. */
export const useConversation = conversationId => {
  const $ = _c(2);
  let t0;
  if ($[0] !== conversationId) {
    t0 = state => state.conversations.find(c => c.id === conversationId) ?? null;
    $[0] = conversationId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useConversationsSelector(t0);
};

/** The open and minimised chat windows. */
export const useOpenConversations = () => {
  return useConversationsSelector(_temp2);
};

/** Whether the conversation list (the chat sidebar) is open. */
export const useConversationListOpen = () => {
  return useConversationsSelector(_temp3);
};

/** The loaded messages of one conversation. */
export const useMessagesState = conversationId => {
  const $ = _c(2);
  let t0;
  if ($[0] !== conversationId) {
    t0 = state => (conversationId ? state.messages[conversationId] : undefined) ?? initialMessagesState;
    $[0] = conversationId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useConversationsSelector(t0);
};

/** How many conversations have unread messages. */
export const useUnreadConversationCount = () => {
  return useConversationsSelector(_temp5);
};

/**
 * A user's presence as the conversations know it, or null when they are in
 * none of them. Selected as a string, so an avatar rerenders only when this
 * one user's presence changes, not on every typing event or message.
 */
export const useParticipantPresence = userId => {
  const $ = _c(8);
  let t0;
  if ($[0] !== userId) {
    t0 = state => {
      if (userId === undefined) {
        return null;
      }
      for (const conversation of state.conversations) {
        const participant = conversation.participants?.find(p => p.id === userId);
        if (participant) {
          const {
            lastActive
          } = participant;
          const lastActiveKey = lastActive instanceof Date ? lastActive.toISOString() : lastActive ?? "";
          return `${participant.isActive ? 1 : 0}|${lastActiveKey}`;
        }
      }
      return null;
    };
    $[0] = userId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const key = useConversationsSelector(t0);
  if (key === null) {
    return null;
  }
  let lastActive_0;
  let t1;
  if ($[2] !== key) {
    const separator = key.indexOf("|");
    lastActive_0 = key.slice(separator + 1);
    t1 = key.slice(0, separator);
    $[2] = key;
    $[3] = lastActive_0;
    $[4] = t1;
  } else {
    lastActive_0 = $[3];
    t1 = $[4];
  }
  const t2 = t1 === "1";
  const t3 = lastActive_0 || null;
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = {
      isActive: t2,
      lastActive: t3
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};

/**
 * The whole conversations state plus every action. Rerenders on any change to
 * any conversation: prefer the hooks above.
 */
export const useConversations = () => {
  const $ = _c(6);
  const actions = useConversationActions();
  const state = useConversationsSelector(_temp6);
  let t0;
  if ($[0] !== state) {
    t0 = conversationId => state.messages[conversationId] || initialMessagesState;
    $[0] = state;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const getMessagesState = t0;
  let t1;
  if ($[2] !== actions || $[3] !== getMessagesState || $[4] !== state) {
    t1 = {
      conversationsState: state,
      ...actions,
      getMessagesState
    };
    $[2] = actions;
    $[3] = getMessagesState;
    $[4] = state;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
export default useConversations;
function _temp(state) {
  return state.conversations;
}
function _temp2(state) {
  return state.openConversations;
}
function _temp3(state) {
  return state.conversationListOpen;
}
function _temp4(c) {
  return !c.read;
}
function _temp5(state) {
  return state.conversations.filter(_temp4).length;
}
function _temp6(s) {
  return s;
}